import { useState, useEffect, useCallback, useMemo } from "react";
import { useCookies } from "react-cookie";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaCookieBite } from "react-icons/fa";
import { updateAnalyticsState } from "../../firebase";

const CookieBanner = () => {
  const { t } = useTranslation();
  const cookieBannerData = useMemo(
    () =>
      t("components.cookieBanner", {
        returnObjects: true,
      }),
    [t]
  );

  const [cookies, setCookie, removeCookie] = useCookies([
    "necessary",
    "analytics",
    "preferences",
    "cookie_consent",
  ]);

  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    preferences: false,
  });

  const [tempPreferences, setTempPreferences] = useState({
    analytics: false,
    preferences: false,
  });

  useEffect(() => {
    if (showSettings) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showSettings]);

  useEffect(() => {
    const newPreferences = {
      necessary: true,
      analytics: cookies.analytics === "true" || cookies.analytics === true,
      preferences:
        cookies.preferences === "true" || cookies.preferences === true,
    };

    setPreferences(newPreferences);

    updateAnalyticsState(newPreferences.analytics);
  }, [cookies]);

  useEffect(() => {
    setShowBanner(!cookies.cookie_consent || cookies.cookie_consent === "null");
  }, [cookies.cookie_consent]);

  useEffect(() => {
    if (showSettings) {
      setTempPreferences({
        analytics: preferences.analytics,
        preferences: preferences.preferences,
      });
    }
  }, [showSettings, preferences]);

  const handleConsent = useCallback(
    (type) => {
      const cookieOptions = {
        path: "/",
        secure: import.meta.env.PROD,
        sameSite: "Lax",
        maxAge: 31536000,
      };

      setCookie("necessary", "true", cookieOptions);
      setCookie("cookie_consent", type, cookieOptions);

      if (type === "necessary") {
        removeCookie("analytics", { path: "/" });
        removeCookie("preferences", { path: "/" });
      } else {
        const analyticsValue =
          type === "all" ? true : tempPreferences.analytics;
        const preferencesValue =
          type === "all" ? true : tempPreferences.preferences;

        const cookiesToSet = {
          analytics: analyticsValue,
          preferences: preferencesValue,
        };

        Object.entries(cookiesToSet).forEach(([key, value]) => {
          if (value) {
            setCookie(key, "true", cookieOptions);
          } else {
            removeCookie(key, { path: "/" });
          }
        });
      }

      const updatedPreferences = {
        necessary: true,
        analytics: type === "all" ? true : tempPreferences.analytics,
        preferences: type === "all" ? true : tempPreferences.preferences,
      };

      updateAnalyticsState(updatedPreferences.analytics);

      setPreferences(updatedPreferences);
      setShowBanner(false);
      setShowSettings(false);
    },
    [setCookie, removeCookie, tempPreferences]
  );

  const togglePreference = useCallback((type) => {
    if (type === "necessary") return;

    setTempPreferences((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, []);

  const bannerAnimationVariants = useMemo(
    () => ({
      hidden: { y: 100, opacity: 0 },
      visible: { y: 0, opacity: 1, transition: { duration: 0.5 } },
      exit: { y: 100, opacity: 0, transition: { duration: 0.3 } },
    }),
    []
  );

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            key="cookie-banner"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={bannerAnimationVariants}
            className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 text-white p-4 shadow-lg"
          >
            <div
              className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4"
              id="safePad"
            >
              <div className="text-left md:text-left w-full md:flex-1">
                {" "}
                <h3 className="text-lg font-semibold mb-2">
                  {cookieBannerData.banner.title}
                </h3>
                <p className="text-sm text-gray-300">
                  {cookieBannerData.banner.description}
                  <br />
                  {cookieBannerData.banner.description1}{" "}
                  <Link to="/cookie-policy" className="text-blue-500 underline">
                    {cookieBannerData.banner.links.cookiePolicy}
                  </Link>{" "}
                  {cookieBannerData.banner.and}{" "}
                  <Link
                    to="/terms-and-conditions"
                    className="text-blue-500 underline"
                  >
                    {cookieBannerData.banner.links.termsAndConditions}
                  </Link>
                  .
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                <button
                  onClick={() => setShowSettings(true)}
                  className="w-full md:w-auto px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors cursor-pointer"
                >
                  {cookieBannerData.banner.buttons.settings}
                </button>
                <button
                  onClick={() => handleConsent("necessary")}
                  className="w-full md:w-auto px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors cursor-pointer"
                >
                  {cookieBannerData.banner.buttons.necessaryOnly}
                </button>
                <button
                  onClick={() => handleConsent("all")}
                  className="w-full md:w-auto px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 rounded-lg text-sm transition-colors cursor-pointer"
                >
                  {cookieBannerData.banner.buttons.acceptAll}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSettings && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 z-50"
              onClick={() => setShowSettings(false)}
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-gray-900 text-white rounded-xl shadow-2xl z-50 max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold">
                    {cookieBannerData.settingsModal.title}
                  </h2>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                    aria-label={cookieBannerData.settingsModal.closeButtonLabel}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                    <div>
                      <h3 className="font-medium">
                        {cookieBannerData.settingsModal.cookies.necessary.title}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">
                        {
                          cookieBannerData.settingsModal.cookies.necessary
                            .description
                        }
                      </p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                        className="sr-only"
                        id="necessary-toggle"
                      />
                      <label
                        htmlFor="necessary-toggle"
                        className="flex items-center cursor-not-allowed"
                      >
                        <div className="relative w-10 h-6 bg-gray-600 rounded-full">
                          <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform translate-x-4"></div>
                        </div>
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                    <div>
                      <h3 className="font-medium">
                        {cookieBannerData.settingsModal.cookies.analytics.title}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">
                        {
                          cookieBannerData.settingsModal.cookies.analytics
                            .description
                        }
                      </p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={tempPreferences.analytics}
                        onChange={() => togglePreference("analytics")}
                        className="sr-only"
                        id="analytics-toggle"
                      />
                      <label
                        htmlFor="analytics-toggle"
                        className="flex items-center cursor-pointer"
                      >
                        <div
                          className={`relative w-10 h-6 ${
                            tempPreferences.analytics
                              ? "bg-[#009fd6]"
                              : "bg-gray-600"
                          } rounded-full transition-colors`}
                        >
                          <div
                            className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${
                              tempPreferences.analytics
                                ? "translate-x-4"
                                : "translate-x-0"
                            }`}
                          ></div>
                        </div>
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                    <div>
                      <h3 className="font-medium">
                        {
                          cookieBannerData.settingsModal.cookies.preferences
                            .title
                        }
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">
                        {
                          cookieBannerData.settingsModal.cookies.preferences
                            .description
                        }
                      </p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={tempPreferences.preferences}
                        onChange={() => togglePreference("preferences")}
                        className="sr-only"
                        id="preferences-toggle"
                      />
                      <label
                        htmlFor="preferences-toggle"
                        className="flex items-center cursor-pointer"
                      >
                        <div
                          className={`relative w-10 h-6 ${
                            tempPreferences.preferences
                              ? "bg-[#009fd6]"
                              : "bg-gray-600"
                          } rounded-full transition-colors`}
                        >
                          <div
                            className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${
                              tempPreferences.preferences
                                ? "translate-x-4"
                                : "translate-x-0"
                            }`}
                          ></div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors cursor-pointer"
                  >
                    {cookieBannerData.settingsModal.buttons.cancel}
                  </button>
                  <button
                    onClick={() => handleConsent("custom")}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 rounded-lg text-sm transition-colors cursor-pointer"
                  >
                    {cookieBannerData.settingsModal.buttons.savePreferences}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <button
        onClick={() => setShowSettings(true)}
        className="fixed bottom-6 left-6 z-40 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 transition-colors cursor-pointer"
        aria-label={cookieBannerData.floatingButton.label}
      >
        <FaCookieBite className="h-5 w-5" />
      </button>
    </>
  );
};

export default CookieBanner;
